import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import {
  GetOrdersSettings,
  UpdateOrdersSettings,
  LoadSplittingRules,
  CreateSplittingRule,
  UpdateSplittingRule,
  DeleteSplittingRule,
} from '../../store/settings.actions';
import {
  IOrdersSettings,
  OrderPriceSource,
  ISplittingRule,
  ISplittingRuleCreate,
} from '../../models/settings.models';

interface IPriceSourceOption {
  value: OrderPriceSource;
  title: string;
  description: string;
  isDefault?: boolean;
}

@Component({
  selector: 'app-orders-settings',
  templateUrl: './orders-settings.component.html',
  styleUrls: ['./orders-settings.component.scss'],
})
export class OrdersSettingsComponent implements OnInit, OnDestroy {
  priceSource: OrderPriceSource = 'volta';
  hidePricingColumns = false;

  readonly priceSourceOptions: IPriceSourceOption[] = [
    {
      value: 'volta',
      title: 'settings.orders_settings.price_source.volta.title',
      description: 'settings.orders_settings.price_source.volta.description',
      isDefault: true,
    },
    {
      value: 'extracted',
      title: 'settings.orders_settings.price_source.extracted.title',
      description: 'settings.orders_settings.price_source.extracted.description',
    },
    {
      value: 'none',
      title: 'settings.orders_settings.price_source.none.title',
      description: 'settings.orders_settings.price_source.none.description',
    },
  ];

  splittingRules: ISplittingRule[] = [];
  splittingRulesLoading = false;
  drawerOpen = false;
  selectedRule: ISplittingRule | null = null;

  private destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    this.store.dispatch(GetOrdersSettings());
    this.store.dispatch(LoadSplittingRules());

    this.store
      .select((state) => state.settings.ordersSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (settings) {
          this.priceSource = settings.default_price_source;
          this.hidePricingColumns = settings.hide_pricing_columns;
        }
      });

    this.store
      .select((state) => state.settings.splittingRules)
      .pipe(takeUntil(this.destroy$))
      .subscribe((rules) => {
        this.splittingRules = rules || [];
      });

    this.store
      .select((state) => state.settings.splittingRulesLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.splittingRulesLoading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectPriceSource(value: OrderPriceSource): void {
    this.priceSource = value;
  }

  onHidePricingColumnsChange(value: boolean): void {
    this.hidePricingColumns = value;
  }

  save(): void {
    const settings: IOrdersSettings = {
      default_price_source: this.priceSource,
      // Only meaningful for the "no price" option; keep it false otherwise so the
      // persisted state matches the UI, where the toggle is not rendered at all.
      hide_pricing_columns: this.priceSource === 'none' ? this.hidePricingColumns : false,
    };
    this.store.dispatch(UpdateOrdersSettings({ settings }));
  }

  restoreDefaults(): void {
    this.priceSource = 'volta';
    this.hidePricingColumns = false;
  }

  openRuleDrawer(rule?: ISplittingRule): void {
    this.selectedRule = rule ?? null;
    this.drawerOpen = true;
  }

  onSaveRule(dto: ISplittingRuleCreate): void {
    if (this.selectedRule) {
      this.store.dispatch(UpdateSplittingRule({ id: this.selectedRule.id, rule: dto }));
    } else {
      this.store.dispatch(CreateSplittingRule({ rule: dto }));
    }
    this.drawerOpen = false;
    this.selectedRule = null;
  }

  onDeleteRule(id: number): void {
    this.store.dispatch(DeleteSplittingRule({ id }));
  }

  onToggleRule(event: { id: number; active: boolean }): void {
    this.store.dispatch(UpdateSplittingRule({ id: event.id, rule: { active: event.active } }));
  }
}
